import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

/**
 * UI-008 — a dialog belongs to the viewport, not to the screen that opened it.
 *
 * `position: fixed` sounds like "relative to the window" and is not: an
 * ancestor carrying `transform`, `filter`, `backdrop-filter`, `perspective`,
 * `contain` or `will-change` becomes the containing block, and every fixed
 * descendant is then laid out and clipped inside *that* box. Every page in
 * this app is wrapped in `.animate-fade-up`, whose animation is declared
 * `both` — so its last keyframe, `transform: translateY(0)` and
 * `filter: blur(0)`, stays applied for as long as the page lives. The wrapper
 * is a containing block permanently, and a dialog rendered inside it centres
 * itself in the content column instead of the window, then hangs off the top
 * once it is taller than that column.
 *
 * Rendering into `document.body` is the fix, and it is the same reason `Modal`
 * and `AnchoredMenu` already do it. Keeping the JSX where it is and hunting
 * the offending ancestor is not: the next page-level animation puts it back.
 */

interface DetailDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** `wide` is for a dialog showing a document rather than a list of fields. */
  size?: 'default' | 'wide';
  children: ReactNode;
  /**
   * Buttons that stay on screen while the body scrolls.
   *
   * A dialog whose content is a document needs this: the document has its own
   * scroll and its own `overscroll-contain`, so a finger that lands on it
   * cannot drag the dialog, and anything below it is then unreachable on a
   * phone. In the footer the buttons are never below anything.
   */
  footer?: ReactNode;
}

export function DetailDialog({ open, onClose, title, size = 'default', children, footer }: DetailDialogProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const { t } = useTranslation();
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/25" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        /* A column of three rather than one scrolling block: the title and the
           way out stay put, the body takes the scroll, and the footer holds
           whatever must not be scrolled past. */
        className={`bg-card rounded-xl shadow-xl w-full max-h-[85vh] flex flex-col overflow-hidden ${
          size === 'wide' ? 'max-w-3xl' : 'max-w-lg'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
          <h3 className="text-base font-semibold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        {footer && (
          <footer className="flex items-center gap-2 px-4 py-3 border-t border-border shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
